/**
 * PaymentsPage — o posto de trabalho do financeiro no ato de pagar.
 *
 * O ato foi desmembrado em três passos, entre dois times:
 *
 *   1. entrada do dinheiro   (financeiro) → o botão "Registrar Pagamento" desta tela
 *   2. marcação de títulos   (cobrança)   → outras telas, ainda por construir
 *   3. efetivação            (financeiro) → "Aprovar" no modal de faturas de cada pagamento
 *
 * O recorte é UM DIA, e não uma paginação livre: o financeiro fecha o dia, não navega histórico.
 * Ainda assim a tabela é o `PageList` das demais telas — o componente traz cabeçalho, ordenação,
 * rodapé e estados de carga/erro já resolvidos, e uma tabela artesanal aqui só criaria uma
 * segunda maneira de ler a mesma coisa.
 *
 * O fuso vai na URL porque `payment_date` é gravado em UTC: sem ele, um PIX das 22h em São Paulo
 * cairia no fechamento do dia seguinte.
 *
 * ⚠️ Enquanto o passo 2 não existir, nenhum pagamento chega a SINALIZADO por meio da aplicação —
 * é um gap conhecido desta entrega, não um defeito da tela.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaClipboard, FaClipboardCheck, FaClipboardList, FaEllipsisV, FaFileInvoiceDollar,
    FaDownload, FaInfoCircle, FaChevronDown, FaChevronRight, FaPlus,
} from 'react-icons/fa';

import PageList from '../../templates/PageList';
import PaymentDatePicker from '../../molecules/payment/PaymentDatePicker';
import PaymentsPanel, { STATUS_META } from './PaymentsPanel';
import RegisterPaymentModal from './modals/RegisterPaymentModal';
import PaymentInvoicesModal from './modals/PaymentInvoicesModal';
import RefreshButton from '../../atoms/RefreshButton';
import PaymentInfoModal from './modals/PaymentInfoModal';
import CommentCell from '../../molecules/CommentCell';
import PaymentMarkingHistoryButton from '../../molecules/payment/PaymentMarkingHistory';
import DeletePaymentDialog from './modals/DeletePaymentDialog';
import EditPaymentModal from './modals/EditPaymentModal';
import { PaymentRow, PaymentStatus } from './services/billingApi';
import appConfig from '../../../config/app-config';
import { formatBRL } from '../../../utils/currencyUtils';
import { formatTaxId } from '../../../utils/taxIdUtils';
import { BRASILIA_TIMEZONE, todayInBrasiliaISO } from '../../../utils/DateUtils';
import { paymentMethodLabel } from '../../../constants/payment';

const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

/** Ícone por estado. Vai com `title` e `aria-label`: ícone sozinho não se lê. */
const STATUS_ICON: Record<PaymentStatus, { Icon: typeof FaClipboard; color: string }> = {
    PENDING: { Icon: FaClipboard, color: 'text-slate-400' },
    MARKED: { Icon: FaClipboardList, color: 'text-amber-500' },
    IDENTIFIED: { Icon: FaClipboardCheck, color: 'text-green-600' },
};

const TYPE_LABEL: Record<string, string> = {
    FINANCIAL: 'Financeiro',
    DEBT_CONFESSION: 'Confissão de Dívida',
    ASSET_DATION: 'Dação de Bens',
};

const RIGHT = 'text-right';

/**
 * Fundo da linha: a REPARTIÇÃO do pagamento, desenhada como preenchimento.
 *
 * ⚠️ Era uma cor sólida por status. Funcionava enquanto um pagamento só podia estar num passo de
 * cada vez; com marcação e efetivação parciais (AF-3671 step 4), um pagamento de 1.000.000 com
 * 200.000 efetivados e 300.000 marcados ficava sem cor nenhuma — "pendente" —, escondendo que
 * metade dele já tinha destino.
 *
 * Agora a linha enche da esquerda: verde até a fração efetivada, âmbar até a marcada, o resto
 * neutro. O usuário lê o andamento de cada pagamento sem abrir nada.
 *
 * Tons com ALPHA, e não as cores cheias: a tabela tem texto em cima, e cor forte aqui competiria
 * com a leitura em vez de guiá-la. Alpha também resolve o dark de graça — a mesma regra funciona
 * sobre fundo claro e escuro, sem um segundo conjunto de cores.
 */
const GREEN = 'rgba(34, 197, 94, 0.20)';    // efetivado — green-500
const AMBER = 'rgba(251, 191, 36, 0.28)';   // marcado, esperando aprovação — amber-400

export const proportionalFill = (row: PaymentRow): string | undefined => {
    const total = row.amount || 0;
    if (!total || row.pendingAmount == null) return undefined;
    const pct = (part: number) => Math.max(0, Math.min(100, ((part || 0) / total) * 100));

    const green = pct(row.paidAmount);
    /**
     * ⚠️ Só entram as fatias com LARGURA. Uma fatia zerada não pode virar parada de cor.
     *
     * A versão anterior emitia as três sempre, e com o efetivado em zero o gradiente começava com
     * `verde 0 0%, âmbar 1% ...` — um ponto percentual em que o navegador interpola entre as duas
     * cores. O resultado era uma sombra verde no começo de uma linha que não tinha nada efetivado:
     * a barra afirmava um estado que não existia.
     */
    const parts = [
        { color: GREEN, width: green },
        { color: AMBER, width: Math.min(100 - green, pct(row.scheduledAmount)) },
    ].filter(part => part.width > 0);

    if (parts.length === 0) return undefined;   // nada com destino: linha limpa, como antes

    /**
     * As paradas se afastam por um ponto percentual em cada fronteira.
     *
     * Coincidindo, a transição é um corte seco — duas faixas encostadas com uma linha dura entre
     * elas. Com a folga, o navegador interpola ali e a passagem fica suave, sem mudar o que a barra
     * AFIRMA: as larguras continuam sendo as frações reais.
     *
     * Só na TABELA. As barras do "Situação do dia" são leitura de proporção, e ali um degradê
     * borraria justamente a fronteira que se quer medir.
     */
    const SOFT = 1;
    const covered = Math.min(100, parts.reduce((sum, part) => sum + part.width, 0));
    // linha cheia não tem para onde desbotar: a folga no fim só apagaria o último ponto da cor
    const fadesOut = covered < 100;

    let cursor = 0;
    const stops = parts.map((part, index) => {
        const from = index === 0 ? 0 : Math.min(100, cursor + SOFT);
        cursor = Math.min(100, cursor + part.width);
        const last = index === parts.length - 1;
        const to = last && !fadesOut ? 100 : Math.max(from, cursor - SOFT);
        // `max` para a folga nunca inverter a parada numa fatia mais estreita que ela
        return `${part.color} ${from}% ${to}%`;
    });
    if (fadesOut) stops.push(`transparent ${Math.min(100, cursor + SOFT)}%`);
    return `linear-gradient(to right, ${stops.join(", ")})`;
};

/** Texto do tooltip da linha — o número por trás das faixas, para quem precisa conferir. */
export const rowSummary = (row: PaymentRow): string => {
    if (row.pendingAmount == null) return STATUS_META[row.status].label;
    return [
        `Identificado: ${formatBRL(row.paidAmount || 0)}`,
        `Sinalizado: ${formatBRL(row.scheduledAmount || 0)}`,
        `A identificar: ${formatBRL(row.pendingAmount)}`,
    ].join(' · ');
};

/**
 * Há sinalização viva neste pagamento?
 *
 * `scheduledCount` e não o valor: uma marcação de R$ 0,00 é dado esquisito, mas existe e ainda
 * assim é uma linha a resolver.
 */
export const hasMarking = (row: PaymentRow): boolean =>
    (row.scheduledCount ?? (row.status === 'MARKED' ? 1 : 0)) > 0;

/** Já há dinheiro efetivado neste pagamento — o que fecha a porta da exclusão. */
export const hasSettled = (row: PaymentRow): boolean =>
    (row.paidCount ?? (row.status === 'IDENTIFIED' ? 1 : 0)) > 0;

const editTooltip = (row: PaymentRow): string =>
    hasSettled(row)
        ? 'Parte deste pagamento já foi efetivada: só o valor pode ser alterado'
        : 'Editar os dados do pagamento';

/**
 * Excluir é possível enquanto NADA foi efetivado.
 *
 * ⚠️ Antes bastava não estar IDENTIFICADO. Com a efetivação parcial um pagamento PENDENTE pode ter
 * fatura já paga, e o servidor recusa a exclusão — a tela oferecia uma ação que ia falhar.
 */
const deleteTooltip = (row: PaymentRow): string =>
    hasSettled(row)
        ? 'Pagamento com fatura já efetivada não pode ser excluído — o settlement já movimentou o título. Nesse caso o caminho é o estorno'
        : hasMarking(row)
            ? 'Excluir o pagamento. As faturas sinalizadas perdem a marcação'
            : 'Excluir o pagamento';

const PaymentsPage: React.FC = () => {
    const [date, setDate] = useState(todayInBrasiliaISO());
    // as linhas que o PageList buscou — o painel reparte as MESMAS, e buscá-las de novo por
    // fora duplicaria a chamada e abriria espaço para os dois divergirem
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [panelOpen, setPanelOpen] = useState(true);
    const [registering, setRegistering] = useState(false);
    const [viewInvoicesOf, setViewInvoicesOf] = useState<PaymentRow | null>(null);
    const [viewInfoOf, setViewInfoOf] = useState<PaymentRow | null>(null);
    const [menuOpen, setMenuOpen] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<PaymentRow | null>(null);
    const [editing, setEditing] = useState<PaymentRow | null>(null);
    // muda a URL para o PageList refazer a busca depois de registrar, editar ou excluir
    const [refreshKey, setRefreshKey] = useState(0);
    // vazio = ordem do servidor (mais recente primeiro), que é como o financeiro trabalha
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

    const fetchHeaders = useMemo(() => [{ key: 'x-api-key', value: API_KEY || '' }], []);

    const url = useMemo(() => {
        /**
         * ⚠️ Fuso de BRASÍLIA, fixo — não o do navegador.
         *
         * O `payment_date` é timestamptz em UTC, e é o fuso que decide em que DIA ele cai. Com o do
         * navegador, o mesmo pagamento das 22h aparecia no fechamento de dias diferentes para quem
         * abre a tela em Campo Grande e para quem abre em São Paulo — duas pessoas conferindo o
         * mesmo banco e discordando sobre a data. O fechamento da operação é o de Brasília.
         */
        const params = new URLSearchParams({ date, tz: BRASILIA_TIMEZONE });
        // o servidor ordena: o dia vem numa página só, mas ordenar no cliente criaria uma segunda
        // semântica de empate e divergiria do `status`, que é derivado lá
        if (sortField) params.set('sort', `${sortOrder === 'DESC' ? '-' : ''}${sortField}`);
        return `${API_URL}/payments?${params}&_r=${refreshKey}`;
    }, [date, refreshKey, sortField, sortOrder]);

    /**
     * Enquanto a busca do PageList está em voo — quem informa é ele, pelo `onLoadingChange`.
     *
     * ⚠️ A primeira tentativa ligava isto no clique e desligava no `onGotApiPayload`, e o botão
     * nunca girava: aquele callback é chamado DURANTE o render, a cada render, com o `data` ANTIGO
     * enquanto a busca nova está em voo — desligava no mesmo instante em que ligava.
     */
    const [loading, setLoading] = useState(false);

    const reload = useCallback(() => setRefreshKey(k => k + 1), []);

    /**
     * Fecha o menu de ações ao clicar em QUALQUER lugar fora dele.
     *
     * Sem isto, abrir o menu e clicar noutro ponto da tela deixava os botões flutuando até alguém
     * clicar de novo no mesmo ícone — e o usuário que abriu na linha errada tinha de voltar nela
     * para se livrar do menu. Esc fecha pelo mesmo motivo.
     */
    useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(null);
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(null); };
        // `mousedown` e não `click`: o clique no próprio ícone já alterna o menu no onClick, e
        // ouvir `click` aqui fecharia e reabriria no mesmo gesto
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const tableHeaders = useMemo(() => [
        // o default do ATable já é text-center; declaramos só quem foge dele.
        // `field` casa com as chaves de _SORT_KEYS no list_payments_query.
        { id: 'status', title: 'Estado', canSort: true, field: 'status' },
        { id: 'amount', title: 'Valor Extrato', canSort: true, field: 'amount', cssClass: RIGHT },
        { id: 'payment_method', title: 'Meio', canSort: true, field: 'payment_method' },
        { id: 'payment_type', title: 'Tipo', canSort: true, field: 'payment_type' },
        { id: 'payer', title: 'Pagador', canSort: true, field: 'payer' },
        {
            id: 'receiving_bank_account', title: 'Conta de Recebimento', canSort: true,
            field: 'receiving_bank_account'
        },
        { id: 'notes', title: 'Mensagem', canSort: false },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const renderRows = (list: any[]) => list.map((row: PaymentRow) => {
        const { Icon, color } = STATUS_ICON[row.status];
        const label = STATUS_META[row.status].label;
        const menuOnThisRow = menuOpen === row.paymentId;
        return (
            <tr key={row.paymentId}
                className={`hover:brightness-95 dark:hover:brightness-110 ${menuOnThisRow ? 'relative z-30' : ''}`}
                // `backgroundImage` e não o atalho `background`: o atalho RESETA as demais
                // propriedades de fundo, e um gradiente é, por definição, uma imagem de fundo
                style={{ backgroundImage: proportionalFill(row) }}
                title={rowSummary(row)}>
                <td className="px-4 py-2 text-center">
                    <Icon className={`inline ${color}`} size={18}
                        title={label} aria-label={label} role="img" />
                </td>
                <td className="px-4 py-2 text-sm text-right font-medium">
                    {row.amount != null ? formatBRL(row.amount) : '-'}
                </td>
                {/* Meio é NULL em confissão de dívida — dado correto, não ausência. Em dação de
                    bens ele carrega o TIPO DO BEM, e é por isso que o rótulo cobre as duas
                    famílias: a coluna responde "por onde entrou". */}
                <td className="px-4 py-2 text-sm text-center">
                    {paymentMethodLabel(row.paymentMethod) || '—'}
                </td>
                <td className="px-4 py-2 text-sm text-center">
                    {TYPE_LABEL[row.paymentType] || row.paymentType}
                </td>
                <td className="px-4 py-2 text-sm text-center max-w-[240px] truncate"
                    title={row.payer?.documentNumber ? formatTaxId(row.payer.documentNumber) : ''}>
                    {row.payer?.name || '—'}
                </td>
                <td className="px-4 py-2 text-sm text-center">
                    {row.receivingBankAccount?.exibitionName || '—'}
                </td>
                <td className="px-4 py-2 text-sm max-w-[240px]">
                    <CommentCell notes={row.notesList || []} />
                </td>
                <td className="px-4 py-2 text-sm text-center whitespace-nowrap">
                    <button onClick={() => setViewInvoicesOf(row)}
                        title={`Faturas deste pagamento (${row.linkedInvoicesCount})`}
                        aria-label="Ver faturas"
                        className="px-2 py-1 text-gray-500 hover:text-blue-600">
                        <FaFileInvoiceDollar />
                    </button>
                    {/* só aparece quando há recibo: botão morto sugere que o arquivo existe e
                        que o download falhou */}
                    {row.fileUrlReceipt && (
                        <a href={row.fileUrlReceipt} target="_blank" rel="noreferrer"
                            title="Baixar recibo" aria-label="Baixar recibo"
                            className="px-2 py-1 text-gray-500 hover:text-blue-600 inline-block">
                            <FaDownload />
                        </a>
                    )}
                    <PaymentMarkingHistoryButton paymentId={row.paymentId} />
                    <button onClick={() => setViewInfoOf(row)}
                        title="Mais informações" aria-label="Mais informações"
                        className="px-2 py-1 text-gray-500 hover:text-blue-600">
                        <FaInfoCircle />
                    </button>
                    <span className="relative inline-block">
                        <button
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => setMenuOpen(m => m === row.paymentId ? null : row.paymentId)}
                            title="Ações do pagamento" aria-label="Ações do pagamento"
                            className="px-2 py-1 text-gray-500 hover:text-blue-600">
                            <FaEllipsisV />
                        </button>
                        {menuOpen === row.paymentId && (
                            /* o `mousedown` que nasce DENTRO do menu não pode fechá-lo antes de o
                               clique chegar ao botão — por isso ele para aqui */
                            <div onMouseDown={e => e.stopPropagation()}
                                className="absolute right-0 z-40 mt-1 w-56 bg-white dark:bg-slate-700 border dark:border-slate-600 rounded shadow-lg text-left">
                                <button onClick={() => { setMenuOpen(null); setEditing(row); }}
                                    title={editTooltip(row)}
                                    className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-slate-600">
                                    Editar Pagamento
                                </button>
                                {/* enquanto nada foi efetivado, o pagamento pode ser desfeito */}
                                <button onClick={() => { setMenuOpen(null); setDeleting(row); }}
                                    disabled={hasSettled(row)}
                                    title={deleteTooltip(row)}
                                    className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed">
                                    Excluir Pagamento
                                </button>
                            </div>
                        )}
                    </span>
                </td>
            </tr>
        );
    });

    return (
        <>
            <PageList
                pageTitle="Pagamentos"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Pagamentos', url: '/billing/payments' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={sortField}
                tableOrderSort={sortOrder}
                onTableSort={(field: string, order: 'ASC' | 'DESC') => {
                    setSortField(field);
                    setSortOrder(order);
                }}
                renderRows={renderRows}
                page={0}
                pageSize={200}
                onPageChange={() => { }}
                onItemsPerPageChange={() => { }}
                itemsLabel="pagamentos"
                onGotApiPayload={(data: any) => setRows(data?.list || [])}
                onLoadingChange={setLoading}
            >
                {{
                    filterSlot: (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mb-1">
                            <button
                                onClick={() => setPanelOpen(a => !a)}
                                aria-expanded={panelOpen}
                                className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200"
                            >
                                {panelOpen ? <FaChevronDown /> : <FaChevronRight />}
                                Situação do dia
                            </button>
                            {panelOpen && (
                                <div className="px-4 pb-4"><PaymentsPanel rows={rows} /></div>
                            )}
                        </div>
                    ),
                    controlSlot: (
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                            <div className="flex items-end gap-2">
                                {/* `PaymentDatePicker` e não o `DateFilter` cru: o seletor abria
                                    sem nenhuma pista de onde havia dinheiro, e escolher o dia era
                                    tentativa e erro. Aqui os dias com pagamento vêm marcados. */}
                                <PaymentDatePicker label="Data do pagamento" value={date} onChange={setDate} />
                                {/* registrar, marcar e aprovar passam por evento: o estado do dia
                                    não muda no instante do clique. O botão fica junto da DATA
                                    porque é o dia selecionado que ele recarrega */}
                                <RefreshButton variant="icon" label="os pagamentos do dia"
                                    busy={loading} onClick={reload} />
                            </div>
                            <button
                                onClick={() => setRegistering(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2 text-sm"
                            >
                                <FaPlus /> Registrar Pagamento
                            </button>
                        </div>
                    ),
                }}
            </PageList>

            {registering && (
                <RegisterPaymentModal defaultDate={date} onClose={() => setRegistering(false)} onRegistered={reload} />
            )}
            {viewInvoicesOf && (
                <PaymentInvoicesModal payment={viewInvoicesOf} onClose={() => setViewInvoicesOf(null)}
                    /* aprovar ou reprovar faturas ali muda o estado do pagamento — sem isto a
                       tabela atrás continuaria mostrando a repartição antiga */
                    onChanged={reload} />
            )}
            {viewInfoOf && (
                <PaymentInfoModal payment={viewInfoOf} onClose={() => setViewInfoOf(null)} />
            )}
            {deleting && (
                <DeletePaymentDialog
                    payment={deleting}
                    onClose={() => setDeleting(null)}
                    onDeleted={reload}
                />
            )}
            {editing && (
                <EditPaymentModal
                    paymentId={editing.paymentId}
                    onClose={() => setEditing(null)}
                    onSaved={reload}
                />
            )}
        </>
    );
};

export default PaymentsPage;
